import {InteractionStatus} from "../model/interactionStatus";
import {CustomerInteraction} from "../model/customerInteraction";
import {Document} from "../model/document";

export default class CustomerInteractionUtils{

  /**
   * Determines if there is a customer interaction with the status 'OPEN' in the provided array of customer interactions
   *
   * @param customerInteractions Array of customer interactions for which should be checked if there is a customer interaction with status 'OPEN'
   * @return True if there is a customer interaction with status 'OPEN' within the provided array of customer interactions else false
   */
  public static hasOpenInteraction(customerInteractions: CustomerInteraction[]): boolean{
    return customerInteractions && customerInteractions.some(customerInteraction => customerInteraction.status === InteractionStatus.OPEN);
  }

  /**
   * Retrieves the latest customer interaction associated with the given document ids from an array of customer interactions
   *
   * @param customerInteractions Array of customer interactions to search for the latest customer interaction
   * @param documents Array of documents for matching their ids against the customer interactions associated ids
   * @return Latest customer interaction associated with the specified document ids or null if none is found
   */
  public static getLatestCustomerInteractionAssociatedWithDocumentIds(customerInteractions: CustomerInteraction[], documents: Document[]): CustomerInteraction {
    const documentIds = documents.map(document => document.docid);
    // Filter all customer interactions based on their status and the provided docuemnt ids
    const relevantCustomerInteractions = customerInteractions
        .filter(customerInteraction => customerInteraction.status === InteractionStatus.OPEN &&
            documentIds.some(documentId => customerInteraction.associatedIds.includes(documentId)));

    if (relevantCustomerInteractions.length === 0) {
      return null;
    }

    relevantCustomerInteractions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    return relevantCustomerInteractions[0];
  }

  /**
   * Builds the notification that is shown to the user after a new customer interaction was created.
   * The content is rendered as HTML, therefore the file names are escaped.
   *
   * @param uploadedFileCount Number of attached files that were uploaded successfully
   * @param failedFileNames Names of the attached files that could not be uploaded
   * @return Content of the notification and whether it should be displayed as an error
   */
  public static buildNewCustomerInteractionNotification(uploadedFileCount: number, failedFileNames: string[]): { content: string, error: boolean } {
    if (failedFileNames && failedFileNames.length > 0) {
      const fileNames = failedFileNames.map(fileName => CustomerInteractionUtils.escapeHtml(fileName)).join(", ");
      return {
        content: `Ihre Anfrage wurde übermittelt, folgende Dateien konnten jedoch nicht hochgeladen werden: ${fileNames}. ` +
            `Bitte öffnen Sie die Anfrage und laden Sie die Dateien erneut hoch.`,
        error: true
      };
    }

    if (uploadedFileCount > 0) {
      const fileText = uploadedFileCount === 1 ? "1 Datei wurde" : `${uploadedFileCount} Dateien wurden`;
      return {content: `Ihre Anfrage wurde erfolgreich übermittelt. ${fileText} erfolgreich hochgeladen.`, error: false};
    }

    return {content: "Ihre Anfrage wurde erfolgreich übermittelt.", error: false};
  }

  /**
   * Escapes HTML special characters of the given text
   *
   * @param text Text that should be escaped
   * @return The escaped text
   */
  private static escapeHtml(text: string): string {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
  }
}
